import { zodResolver } from '@hookform/resolvers/zod';
import ArrowForward from '@kmx/mui-icons/KmxArrowRightFilled';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { licenseOrVinSchema, stateSchema, zipSchema } from '../../utils/schemas';
import usaStates from '../../utils/states';

const schema = z.object({
    licenseVin: licenseOrVinSchema,
    zipCode: zipSchema,
    state: stateSchema,
});

type FormValues = z.infer<typeof schema>;

interface WidgetProps {
    baseUrl?: string;
    originPage?: string;
    redirectPath?: string;
}
const Widget = ({ baseUrl = '', originPage = '', redirectPath = '/sell-my-car' }: WidgetProps) => {
    const theme = useTheme();
    const useSmallStyles = useMediaQuery(theme.breakpoints.down('sm'));
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({ mode: 'onTouched', resolver: zodResolver(schema) });

    const onFormSubmit = useCallback(
        (data: FormValues) => {
            const { licenseVin, state, zipCode } = data;

            let providedBaseUrl = baseUrl;
            if (!baseUrl && window?.location?.origin) {
                providedBaseUrl = window.location.origin;
            }
            const url = new URL(redirectPath, providedBaseUrl);

            if (originPage) {
                url.searchParams.append('originPage', originPage);
            }

            if (licenseVin.length > 10) {
                url.searchParams.append('vin', licenseVin);
            } else {
                url.searchParams.append('plate', licenseVin);
            }

            url.searchParams.append('state', state);
            url.searchParams.append('zipcode', zipCode);

            if (window) {
                window.open(url, '_self');
            }
        },
        [baseUrl, originPage, redirectPath]
    );

    return (
        <Paper
            sx={{
                maxWidth: '480px',
                padding: { xs: 4, md: 5 },
                marginX: 'auto',
                marginY: 1,
            }}
            variant="outlined"
            elevation={1}
        >
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Stack gap={3}>
                    <Stack gap={0.5}>
                        <Typography component="h6" variant="h6">
                            Sell/trade in
                        </Typography>
                        {useSmallStyles ? (
                            <>
                                <Typography component="h5" variant="h5">
                                    Real offers, really fast
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography component="h4" variant="h4">
                                    Real offers, really fast
                                </Typography>
                            </>
                        )}
                    </Stack>
                    <Stack gap={2}>
                        <TextField
                            autoCapitalize="characters"
                            autoComplete="off"
                            error={!!errors.licenseVin}
                            fullWidth
                            helperText={errors.licenseVin?.message}
                            label="License plate or VIN"
                            required
                            slotProps={{
                                htmlInput: {
                                    minLength: 1,
                                    maxLength: 17,
                                    pattern: '[0-9a-zA-Z \\-]+',
                                },
                                inputLabel: { required: false },
                            }}
                            {...register('licenseVin')}
                        />
                        <Stack direction={'row'} gap={1}>
                            <TextField
                                autoComplete="off"
                                error={!!errors.zipCode}
                                fullWidth
                                helperText={errors.zipCode?.message}
                                label="ZIP Code"
                                required
                                slotProps={{
                                    htmlInput: { maxLength: 5, minLength: 5 },
                                    inputLabel: { required: false },
                                }}
                                {...register('zipCode')}
                            />
                            <TextField
                                error={!!errors.state}
                                label="State"
                                fullWidth
                                helperText={errors.state?.message}
                                required
                                select
                                slotProps={{
                                    select: { native: true },
                                    inputLabel: { required: false },
                                }}
                                {...register('state')}
                            >
                                <option hidden value=""></option>
                                {usaStates.map(state => (
                                    <option key={state.value} value={state.value}>
                                        {state.label}
                                    </option>
                                ))}
                            </TextField>
                        </Stack>
                    </Stack>
                    <Button type="submit" size="large" endIcon={<ArrowForward />} variant="contained">
                        Get my offer
                    </Button>
                </Stack>
            </form>
        </Paper>
    );
};

export default Widget;
